import * as React from 'react';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { Button, LinearProgress } from '@mui/material';
import { AlumnoType } from '../../types/types';
import hash from 'object-hash';
import { ReporteDeudaType, ReportePagosType, useGetReporteDeuda } from '../../hooks/getDeuda';
import { useContext, useEffect, useState } from 'react';
import { AddPagosPayloadType, UseAddPAgo } from '../../hooks/addPAgo';
import { UserContext } from '../../contexts/userContext';
import { NewPago, PagoDialog } from './DialogPago';
import dayjs from 'dayjs';
import { monthToString } from '../../utils/utils';
import FullScreenDialog from '../FullScreenDialog';
import { UseGetHnos } from '../../hooks/hermanos';
import { TablaFamiliarPagos } from './tablaPagosFamiliar';
import { ReciboFamiliar } from './ReciboFamiliar';


export interface DateReportProps {
  alumno: AlumnoType | null;
  refetchLista: () => void;
  verHistorico: boolean;
}

export interface DeudaFamiliarType {
  cuotas: ReporteDeudaType[],
  periodo: string,
  totalAPAgar: number,
  totalPagado: number,
  totalAdeudado: number,
  totalOriginal: number,
}

export const emptyPagoFamiliar = {
  open: false,
  anio: 0,
  mes: 0,
  user: '',
  monto: 0,
  montoEfectivo: 0,
  montoTransferencia: 0,
  obs: '',
  alumnoId: '',
  fecha: new Date().toISOString(),
  transferencia: false,
  saldado: false,
  cuotas: [] as ReporteDeudaType[],
};

export default function DeudaReportFamiliar(props: DateReportProps) {
  const { alumno, refetchLista, verHistorico } = props;
  const { user } = useContext(UserContext);
  const [pago, setPago] = useState<AddPagosPayloadType[] | null>(null);
  const [nuevoPago, setNuevoPago] = useState<NewPago>(emptyPagoFamiliar);
  const [dialogPagos, setDialogPagos] = useState<ReportePagosType[] | null>(null);
  const addPago = UseAddPAgo(pago, user);
  const hermanos = UseGetHnos(alumno?._id, user);
  const deudaFamiliar = useGetReporteDeuda(hermanos.data, user, verHistorico);

  const [recibo, setRecibo] = useState<DeudaFamiliarType | null>(null);

  const pagarCuotas = () => {
    const newPago:AddPagosPayloadType[] = [];
    let montoRestante = nuevoPago.monto;
    if (!nuevoPago.cuotas || montoRestante < 0) return;
    for (const cuota of nuevoPago.cuotas) {
      if (cuota.monto){
        let monto = 0;
        const totalPagado = cuota.pagos.reduce((t, p) => t + p.importe, 0);
        const cuotaBase = nuevoPago.transferencia
          ? (cuota.montoTransferencia ?? cuota.monto)
          : cuota.monto;
        const deuda = cuotaBase - totalPagado;
        if (deuda < montoRestante) monto = deuda;
        else monto = montoRestante;
        if (monto > 0) {
          newPago.push({
            ...nuevoPago,
            monto,
            anio: cuota.year,
            mes: cuota.month,
            user: user?._id ?? '',
            alumnoId: cuota.alumnoId,
          });
        }
        montoRestante -= monto;
        if (montoRestante <= 0) break;
      }
    }
    setPago(newPago);
  };

  useEffect(() => {
    if (addPago.data && !addPago.isLoading) {
      deudaFamiliar.refetch();
      setNuevoPago(emptyPagoFamiliar);
    }
  }, [addPago.data]);

  useEffect(() => {
    if ((addPago.data && !addPago.isFetching)) {
      setTimeout(() => refetchLista(), 500);
    }
  }, [addPago.data]);

  if (!alumno) return null;
  if (!user?._id) return null;

  const deudasAgrup = deudaFamiliar.data?.filter((p) => !p.esMatricula).reduce((group:any, rep) => {
    const { year, month } = rep;
    group[`${month}-${year}`] = group[`${month}-${year}`] ?? [];
    group[`${month}-${year}`].push(rep);
    return group;
  }, {});

  const deudasAgrupadasConValores:DeudaFamiliarType[] = [];

  if (deudasAgrup) {
    for (const key in deudasAgrup) {
      const periodo:ReporteDeudaType[] = deudasAgrup[key];
      const totalOriginal = periodo.reduce((t:number, p) => p.montoOriginal ? t += + p.montoOriginal : t, 0);
      const totalAPAgar = periodo.reduce((t:number, p) => p.monto ? t += + p.monto : t, 0);
      const totalPagado = periodo.reduce((t:number, p) => p.pagos ? t += + p.pagos.reduce((pt, pp) => pt += pp.importe, 0) : t, 0);
      const totalAdeudado = totalAPAgar - totalPagado;
      deudasAgrupadasConValores.push({ cuotas: periodo, totalAPAgar, totalPagado, totalAdeudado, totalOriginal, periodo: key });
    }
  }

  const marcarSaldado = (cuotas: ReporteDeudaType[]) => {
    const newPago:AddPagosPayloadType[] = [];
    for (const cuota of cuotas) {
      newPago.push({
        ...nuevoPago,
        monto: 0,
        anio: cuota.year,
        mes: cuota.month,
        obs: 'Marcado como saldado',
        saldado: true,
        user: user?._id ?? '',
        alumnoId: cuota.alumnoId,
      });
    }
    setPago(newPago);
  };

  const columns: GridColDef[] = [
    {
      field: 'periodo',
      headerName: 'Periodo',
      valueGetter: (params) => {
        const date = params.row.periodo.split('-');
        return dayjs(`${date[1]}/${date[0]}/01`).valueOf();
      },
      headerClassName: 'table-headers',
      width: 200,
      renderHeader: () => (<strong>Periodo</strong>),
      renderCell: (params: { row: DeudaFamiliarType }) =>{
        const date = params.row.periodo.split('-');
        const monto = params.row.totalAPAgar;
        const totalPagado = params.row.totalPagado;
        const saldado = totalPagado >= monto;
        const deuda = monto - totalPagado;
        return (
          <div>
            <div style={{ fontWeight: 'bold', marginTop: 16 }}>
              {`${monthToString(Number(date[0]))} de ${date[1]}`}
            </div>
            <Button
              fullWidth
              variant='contained'
              size='small'
              color={deuda > 0 && !saldado ? 'error' : 'success'}
              onClick={() => setNuevoPago((prev) => ({
                ...prev,
                open: true,
                cuotas: params.row.cuotas,
                monto: params.row.totalAdeudado,
                montoEfectivo: params.row.cuotas.reduce((total, cuota) => total + cuota.monto, 0),
                montoTransferencia: params.row.cuotas.reduce((total, cuota) => total + (cuota.montoTransferencia ?? cuota.monto), 0),
              }))}
              style={{ fontSize: 'x-small', marginTop: 16, marginBottom: 8 }}
            >
              {deuda > 0 && !saldado ? <>Registrar pago</> : <>Saldado</>}
            </Button>
            {deuda > 0 && !saldado && <Button
              fullWidth
              variant='contained'
              size='small'
              onClick={() => {marcarSaldado(params.row.cuotas);}}
              style={{ fontSize: 'x-small', marginTop: 16, marginBottom: 8 }}
            >
              Marcar como saldado
            </Button>}
            {params.row.cuotas.length > 0 &&
            <Button
              fullWidth
              variant='contained'
              size='small'
              onClick={() => setRecibo(params.row)}
              style={{ fontSize: 'x-small', marginBottom: 16 }}
            >
              Generar Recibo
            </Button>
            }
          </div>
        );
      },
    },
    {
      field: 'pagos',
      headerName: 'Pagos',
      headerClassName: 'table-headers',
      flex: 1,
      sortable: false,
      renderHeader: () => (<strong>Pagos</strong>),
      minWidth: 550,
      renderCell: (params) => <TablaFamiliarPagos deudaFamiliar={params.row} />,
    },
  ];

  if (!deudaFamiliar.data || deudaFamiliar.isLoading || !hermanos.data || !deudasAgrupadasConValores) return <LinearProgress />;

  return (
    <>
      <ReciboFamiliar onClose={() => {setRecibo(null);}} pagos={recibo} alumno={alumno} />
      <FullScreenDialog titulo="Lista de pagos" open={!!dialogPagos} onClose={() => setDialogPagos(null)}>
      </FullScreenDialog>
      <PagoDialog
        isLoading={addPago.isLoading}
        refetch={() => pagarCuotas()}
        nuevoPago={nuevoPago}
        setNuevoPago={setNuevoPago}
        montoEfectivo={nuevoPago.montoEfectivo}
        montoTransferencia={nuevoPago.montoTransferencia}
      />
      <DataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: 'periodo', sort: 'desc' }],
          },
        }}
        autoHeight
        getRowId={(row) =>  hash(row)}
        getRowHeight={() => 'auto'}
        rows={deudasAgrupadasConValores}
        columns={columns}
        components={{ Toolbar: GridToolbar }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        hideFooter
      />
    </>
  );
}
